import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@app/components/common/Card/Card';
import { BaseChart } from '@app/components/common/charts/BaseChart';
import { Col, DatePicker, Row, Spin } from 'antd';
import dayjs from 'dayjs';
import { Select } from '@app/components/common/selects/Select/Select.styles';
import { Option } from '@app/components/common/selects/Select/Select';
import { getStatistic as getStatisticFac } from '@app/api/fac/issue.api';
import { getStatistic as getStatisticIqc } from '@app/api/iqc/issue.api';
import { getStatistic as getStatisticInt } from '@app/api/int/issue.api';
import { getStatistic as getStatisticSpa } from '@app/api/spa/issue.api';
import { getStatistic as getStatisticCbm } from '@app/api/cbm/issue.api';
import { getStatistic as getStatisticWh } from '@app/api/wh/issue.api';

const statusList = ['Open', 'Progress', 'Closed'];
const issueList = ['Safety', 'Quality', 'Delivery', 'Productivity', 'Utility', 'Machine', 'Others'];
const { RangePicker } = DatePicker;

export const BarAllIssueStatusChart: React.FC = ({
  dataPropFac,
  dataPropInt,
  dataPropCbm,
  dataPropIqc,
  dataPropWh,
  dataPropSpa,
  selectedMonth,
}: {
  dataPropFac: any;
  dataPropInt: any;
  dataPropCbm: any;
  dataPropIqc: any;
  dataPropWh: any;
  dataPropSpa: any;
  selectedMonth: number;
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<{
    Open: number[];
    Progress: number[];
    Closed: number[];
  }>({
    Open: [],
    Progress: [],
    Closed: [],
  });
  const [timeMonth, setTimeMonth] = useState<number[]>([
    dayjs().startOf('month').valueOf(),
    dayjs().endOf('month').valueOf(),
  ]);
  const [status, setStatus] = useState<'Open' | 'Progress' | 'Closed' | ''>();
  const [issue, setIssue] = useState<
    'Safety' | 'Quality' | 'Delivery' | 'Productivity' | 'Utility' | 'Machine' | 'Others' | ''
  >();
  const dataFactory = useRef<{
    dataFac: any[];
    dataInt: any[];
    dataIqc: any[];
    dataCbm: any[];
    dataWh: any[];
    dataSpa: any[];
  }>();
  const [isLoading, setLoading] = useState(false);

  const handleGetData = async (
    createdAtMin: number,
    createdAtMax: number,
    statusFilter: 'Open' | 'Progress' | 'Closed' | '' | undefined,
  ) => {
    try {
      setLoading(true);
      const req: any = {
        createdAtMin,
        createdAtMax,
      };
      const [resFac, resInt, resIqc, resCbm, resWh, resSpa] = await Promise.all([
        getStatisticFac(req),
        getStatisticInt(req),
        getStatisticIqc(req),
        getStatisticCbm(req),
        getStatisticWh(req),
        getStatisticSpa(req),
      ]);

      dataFactory.current = {
        dataFac: resFac.data,
        dataInt: resInt.data,
        dataIqc: resIqc.data,
        dataCbm: resCbm.data,
        dataWh: resWh.data,
        dataSpa: resSpa.data,
      };
      const allData = [
        dataFactory.current.dataSpa,
        dataFactory.current.dataInt,
        dataFactory.current.dataCbm,
        dataFactory.current.dataIqc,
        dataFactory.current.dataWh,
        dataFactory.current.dataFac,
      ];
      const { Open, Progress, Closed } = handleTransformData(allData, statusFilter);
      setData({ Open, Progress, Closed });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleTransformData = (allData: any[], statusFilter: 'Open' | 'Progress' | 'Closed' | '' | undefined) => {
    const Open: number[] = [];
    const Progress: number[] = [];
    const Closed: number[] = [];

    if (!statusFilter) {
      const totalCounts = allData.map((dataArray) => {
        return dataArray.reduce(
          (acc, item) => {
            acc.countOpen +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusOpen || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusOpen || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusOpen || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusOpen || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusOpen || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusOpen || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusOpen || 0 : 0);

            acc.countProcess +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusProcessing || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusProcessing || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusProcessing || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusProcessing || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusProcessing || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusProcessing || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusProcessing || 0 : 0);

            acc.countClosed +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusClose || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusClose || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusClose || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusClose || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusClose || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusClose || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusClose || 0 : 0);

            return acc;
          },
          {
            countOpen: 0,
            countProcess: 0,
            countClosed: 0,
          },
        );
      });
      for (let index = 0; index < totalCounts.length; index++) {
        const element = totalCounts[index];
        Open.push(element.countOpen);
        Progress.push(element.countProcess);
        Closed.push(element.countClosed);
      }
    }

    if (statusFilter === 'Open') {
      const openCounts = allData.map((dataArray) => {
        return dataArray.reduce(
          (acc, item) => {
            acc.countOpen +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusOpen || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusOpen || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusOpen || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusOpen || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusOpen || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusOpen || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusOpen || 0 : 0);

            return acc;
          },
          {
            countOpen: 0,
          },
        );
      });
      console.log(openCounts);
      for (let index = 0; index < openCounts.length; index++) {
        const element = openCounts[index];
        Open.push(element.countOpen);
      }
    }
    if (statusFilter === 'Progress') {
      const processingCounts = allData.map((dataArray) => {
        return dataArray.reduce(
          (acc, item) => {
            acc.countProcess +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusProcessing || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusProcessing || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusProcessing || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusProcessing || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusProcessing || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusProcessing || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusProcessing || 0 : 0);

            return acc;
          },
          {
            countProcess: 0,
          },
        );
      });
      for (let index = 0; index < processingCounts.length; index++) {
        const element = processingCounts[index];
        Progress.push(element.countProcess);
      }
    }
    if (statusFilter === 'Closed') {
      const closeCounts = allData.map((dataArray) => {
        return dataArray.reduce(
          (acc, item) => {
            acc.countClosed +=
              (issue === 'Safety' || !issue ? item.countSafetyStatusClose || 0 : 0) +
              (issue === 'Utility' || !issue ? item.countUtilityStatusClose || 0 : 0) +
              (issue === 'Machine' || !issue ? item.countMachineStatusClose || 0 : 0) +
              (issue === 'Others' || !issue ? item.countOtherIssueStatusClose || 0 : 0) +
              (issue === 'Quality' || !issue ? item.countQualityStatusClose || 0 : 0) +
              (issue === 'Delivery' || !issue ? item.countDeliveryStatusClose || 0 : 0) +
              (issue === 'Productivity' || !issue ? item.countProductivityStatusClose || 0 : 0);
            return acc;
          },
          {
            countClosed: 0,
          },
        );
      });
      for (let index = 0; index < closeCounts.length; index++) {
        const element = closeCounts[index];
        Closed.push(element.countClosed);
      }
    }

    return { Open, Progress, Closed };
  };

  useEffect(() => {
    if (timeMonth[0] === dayjs().startOf('month').unix() * 1000) {
      const allData = [dataPropSpa, dataPropInt, dataPropCbm, dataPropIqc, dataPropWh, dataPropFac];
      const { Open, Progress, Closed } = handleTransformData(allData, status);
      setData({ Open, Progress, Closed });
    } else if (dataFactory.current) {
      const allData = [
        dataFactory.current.dataSpa,
        dataFactory.current.dataInt,
        dataFactory.current.dataCbm,
        dataFactory.current.dataIqc,
        dataFactory.current.dataWh,
        dataFactory.current.dataFac,
      ];
      const { Open, Progress, Closed } = handleTransformData(allData, status);
      setData({ Open, Progress, Closed });
    }
  }, [status, issue]);

  const grid = {
    left: 100,
    right: 100,
    top: 50,
    bottom: 50,
  };

  const series = (): echarts.BarSeriesOption[] =>
    ['Open', 'Progress', 'Closed'].map((name, sid) => {
      return {
        name,
        type: 'bar',
        stack: 'total',
        barWidth: '60%',
        label: {
          show: true,
          formatter: (params) => (params.value > 0 ? params.value.toString() : ''),
        },
        data: data[name],
      };
    });

  const option = {
    legend: {
      left: 20,
      top: 0,
    },
    grid,
    toolbox: {
      feature: {
        dataView: { show: true, readOnly: false },
        magicType: { show: true, type: ['bar'] },
        restore: { show: true },
        saveAsImage: { show: true },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
    },
    yAxis: {
      type: 'value',
    },
    xAxis: {
      type: 'category',
      data: ['SPA', 'INT', 'CBM', 'IQC', 'WH', 'FAC'],
    },
    series: series(),
  };

  const onChangeSelectTime = (event: any) => {
    console.log('onChangeSelectTime ', event);
    if (event) {
      setTimeMonth([event[0].startOf('month').valueOf(), event[1].endOf('month').valueOf()]);
      handleGetData(event[0].startOf('month').valueOf(), event[1].endOf('month').valueOf(), status);
    }
  };

  const onChangeStatus = (event: any) => {
    console.log('onChangeGroup ', event);
    setStatus(event);
  };

  const onChangeIssue = (event: any) => {
    console.log('onChangeIssue ', event);
    setIssue(event);
  };

  return (
    <>
      <Spin spinning={isLoading}>
        <Card padding="0 0 1.875rem">
          <Row
            title={t('charts.barLabel')}
            style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 20, marginBottom: 15 }}
          >
            <Col offset={1} span={7}>
              <label>{t('common.issueGroup')}</label>
              <Select defaultValue="" style={{ minWidth: 200, marginLeft: 10 }} onChange={onChangeIssue}>
                <Option value="">{t('wh.common.all')}</Option>
                {issueList.map((val) => (
                  <Option key={val} value={val}>
                    {val}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label>{t('common.status')}</label>
              <Select defaultValue="" style={{ minWidth: 200, marginLeft: 10 }} onChange={onChangeStatus}>
                <Option value="">{t('wh.common.all')}</Option>
                {statusList.map((val) => (
                  <Option key={val} value={val}>
                    {val}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label>{t('common.selectmonthyear')}</label>
              <RangePicker
                defaultValue={[dayjs(selectedMonth).startOf('month'), dayjs(selectedMonth).endOf('month')]}
                style={{ marginLeft: 10 }}
                format="MM/YYYY"
                picker="month"
                onChange={onChangeSelectTime}
              />
            </Col>
          </Row>
          <hr />
          <BaseChart option={option} style={{ marginTop: 10 }} />
        </Card>
      </Spin>
    </>
  );
};
